// `m8t platform converge` — the headless, Managed-Identity-authenticated
// updater-job driver. This is the ORCHESTRATOR that wires together
// the converge modules; it does NOT reimplement the
// converge engine — it drives it.
//
// One tick of the loop:
//   1. Resolve the headless context from env (MI credential, sub, RG, endpoint,
//      channel URL).
//   2. Claim the pending apply-request row (pending → claimed, ETag-guarded).
//      No pending row → empty tick, exit 0.
//   3. Forward-only guard: refuse a downgrade against the installed stamp.
//   4. Self-fetch + validate the TARGET manifest from the channel (never trust
//      the row for content).
//   5. Self-update compat hop: if the baked engine is below the manifest's
//      minimum CLI, bump the updater job's OWN image, mark awaiting-engine-update,
//      exit 0 — the next tick re-claims with the new engine.
//   6. Apply: resolve project/content/tree/suffix/gateway, build the CLI-wired
//      ConvergeDeps, compute the forward plan, and hand the whole thing to the
//      health-gate-with-rollback wrapper — which applies, gates only what
//      changed, and rolls back exactly once on failure. success → 0, else 1.
//
// The pure `isForwardTarget` is the one fully unit-testable piece (see
// converge.test.ts). The full execute() orchestration drives live Azure/Foundry
// and is proven by a live end-to-end update run, kept correct-by-inspection here.

import type { TableClient } from "@azure/data-tables";
import { isInFlight } from "@m8t-stack/platform-release";
import { Command } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import { compareSemver } from "../../lib/platform-update.js";
import { resolveHeadlessContextFromEnv, type HeadlessContext } from "../../lib/platform-converge-mi.js";
import { openApplyTable, claimApplyRequest, patchApplyRequest, readApplyRequest } from "../../lib/apply-request-store.js";
import { fetchManifest, manifestSourceForVersionTag } from "../../lib/release-channel.js";
import { resolveReleaseContentWithAgents, type RepoTree } from "../../lib/release-content.js";
import { assertBakedContentPresent, assertForwardTarget } from "../../lib/rail-preconditions.js";
import { tryResolveRepoRoot } from "../../lib/brain-paths.js";
import { diffPlan, type ApplyCtx } from "../../lib/platform-converge.js";
import { buildConvergeDeps, assertCliVersionOk } from "../../lib/platform-converge-cli.js";
import { readStamp, type InstalledStamp } from "../../lib/platform-stamp.js";
import { openInfraParamsTable, readInfraParams, deriveSuffixVerified } from "../../lib/platform-infra-params.js";
import { resolveFoundryProject } from "../../lib/foundry-project.js";
import { CLI_VERSION } from "../../lib/package-version.js";
import { needsEngineUpdate, updateOwnJobImage, readBakedReleaseVersion } from "../../lib/platform-self-update.js";
import { runHealthGateWithRollback } from "../../lib/platform-health-gate.js";
import { runAz } from "../../lib/az.js";
import { LocalCliError } from "../../lib/errors.js";

/**
 * Normalize a platform version string to the `vX.Y.Z` form `compareSemver`
 * expects. Handles BOTH shapes the two sides of the forward-only guard carry:
 *   - the apply-request `target` is a BARE version ("0.2.0") — the web button
 *     writes `state.target.version`.
 *   - the stamp's `platformVersion` is the TAG form ("platform-v0.1.0") — it is
 *     set to `manifest.platform.tag`.
 * Strip a leading `platform-` (tag prefix), then ensure the `v` prefix.
 */
function canonicalVersion(v: string): string {
  const bare = v.replace(/^platform-/, "");
  return bare.startsWith("v") ? bare : `v${bare}`;
}

/**
 * Forward-only guard (pure, unit-tested). Accepts when the target is newer than
 * OR equal to the installed version (an idempotent re-apply is allowed), and
 * always accepts on an unconverged install (`installed === null`). Refuses a
 * strict downgrade — the updater NEVER rolls the platform backwards through this
 * door (a health-gate rollback is a different, exempt door).
 */
export function isForwardTarget(target: string, installed: string | null): boolean {
  if (!installed) return true;
  return compareSemver(canonicalVersion(target), canonicalVersion(installed)) >= 0;
}

/**
 * FIX A's backstop write: patch the apply-request row to `status:"failed"` —
 * but ONLY if it is still sitting `"claimed"`/`"applying"` (i.e. `isInFlight`).
 * The setup region this guards runs entirely before any terminal write, so in
 * the normal case the row is exactly where the claim/applying writes left it.
 * The guard exists so this backstop can never clobber a terminal status some
 * inner code already set (there is none in the guarded region today, but the
 * re-read makes that an invariant rather than an accident of call order).
 * Re-reads rather than trusting a passed-in status, since the row is the
 * source of truth and may have moved (e.g. a concurrent operator action).
 */
async function failIfStillInFlight(client: TableClient, now: () => string, error: string): Promise<void> {
  const cur = await readApplyRequest(client);
  if (!cur || !isInFlight(cur.status)) return;
  await patchApplyRequest(client, (r) => ({
    ...r,
    status: "failed",
    result: { appliedVersion: null, error },
    updatedAt: now(),
  }));
}

export class PlatformConvergeCommand extends M8tCommand {
  static paths = [["platform", "converge"]];
  static usage = Command.Usage({
    category: "Platform",
    description: "Headless updater-job driver: claim a pending apply-request and converge the platform to it.",
    details:
      "The Managed-Identity-authenticated entry point run by the updater Container Apps job. One invocation is one tick: it claims the pending apply-request row (if any), refuses a downgrade against the installed stamp, self-fetches and validates the target manifest, self-updates its own engine image when the manifest requires a newer CLI, then drives the converge engine behind the health-gate + auto-rollback. Reads its subscription/RG/endpoint/channel from env (MI_CLIENT_ID, SUBSCRIPTION_ID, RESOURCE_GROUP, FOUNDRY_ENDPOINT, M8T_UPDATE_CHANNEL_URL). Not intended for interactive use — the founder-facing path is 'm8t platform update'.",
  });

  async executeCommand(): Promise<number> {
    const ctx = resolveHeadlessContextFromEnv(process.env);
    const executionId = process.env.CONTAINER_APP_JOB_EXECUTION_NAME ?? `exec-${process.pid.toString()}`;
    const now = (): string => new Date().toISOString();
    const log = (m: string): void => {
      this.context.stdout.write(m + "\n");
    };

    const client = await openApplyTable({
      credential: ctx.credential,
      subscriptionId: ctx.subscriptionId,
      resourceGroup: ctx.resourceGroup,
    });

    // 1. Claim (pending → claimed, ETag-guarded). No pending row (or lost the
    //    race) → empty tick, exit 0. The lease is 30 minutes — one converge.
    const claimed = await claimApplyRequest(client, executionId, now(), 30 * 60 * 1000);
    if (!claimed) {
      log("no pending apply-request — nothing to converge.");
      return 0;
    }
    log(`claimed apply-request → target ${claimed.target} (execution ${executionId}).`);

    // 2. Forward-only guard against the installed stamp.
    const stamp = await readStamp({
      credential: ctx.credential,
      subscriptionId: ctx.subscriptionId,
      resourceGroup: ctx.resourceGroup,
    });
    const installed = stamp ? stamp.platformVersion : null;
    if (!isForwardTarget(claimed.target, installed)) {
      await patchApplyRequest(client, (r) => ({
        ...r,
        status: "failed",
        result: { appliedVersion: null, error: `refused downgrade ${installed ?? "(none)"} → ${claimed.target}` },
        updatedAt: now(),
      }));
      log(`refused: target ${claimed.target} is a downgrade from installed ${installed ?? "(none)"}.`);
      return 1;
    }

    // 3. Self-fetch + validate the TARGET manifest from the channel. Never trust
    //    the row for content: fetch the manifest PINNED to exactly the claimed
    //    target version (not "latest" — the channel may have advanced past what
    //    the button claimed), honoring a custom bicep-set channel host, then
    //    assert the fetched manifest is in fact the claimed target.
    const manifest = await fetchManifest(targetManifestSource(ctx, claimed.target));
    if (manifest.platform.version !== claimed.target) {
      await patchApplyRequest(client, (r) => ({
        ...r,
        status: "failed",
        result: { appliedVersion: null, error: `channel manifest is version ${manifest.platform.version}, expected the claimed target ${claimed.target}` },
        updatedAt: now(),
      }));
      log(`refused: fetched manifest version ${manifest.platform.version} ≠ claimed target ${claimed.target}.`);
      return 1;
    }

    // 4. Self-update hop (with the baked-content fix). The job applies
    //    release content from the repo BAKED into its own image (/opt/m8t/repo),
    //    resolved via the ~/.m8t/repo-root marker — NOT a per-commit fetch
    //    (the repo is private and the job has no GitHub auth). So the
    //    running image's baked installer release MUST match the target manifest's
    //    installer, or its baked persona/infra content is from the wrong release.
    //    needsEngineUpdate fires when it doesn't (primary) OR when the baked CLI
    //    is below the manifest minimum (secondary). Either way: bump the updater
    //    job's OWN image to the target installer, mark awaiting-engine-update, and
    //    exit 0 — the next tick re-claims (apply-request-store treats that state
    //    as claimable) with the correct baked engine+content. Terminates by
    //    construction: after the hop the baked release EQUALS the target, so the
    //    next tick's needsEngineUpdate returns false and it proceeds to apply.
    const repoRoot = tryResolveRepoRoot();
    const bakedRelease = readBakedReleaseVersion(repoRoot);
    if (needsEngineUpdate(manifest, CLI_VERSION, bakedRelease)) {
      log(
        `self-updating the updater image (baked release ${bakedRelease ?? "(none)"} / engine ${CLI_VERSION} → ` +
          `target installer ${manifest.components.installer.version}, cli.min ${manifest.components.cli.min}).`,
      );
      await updateOwnJobImage({
        subscriptionId: ctx.subscriptionId,
        resourceGroup: ctx.resourceGroup,
        manifest,
      });
      await patchApplyRequest(client, (r) => ({
        ...r,
        status: "awaiting-engine-update",
        phase: "updating the updater engine image",
        updatedAt: now(),
      }));
      log("updater image bumped — awaiting-engine-update. The next tick re-claims with the new engine.");
      return 0;
    }
    assertCliVersionOk(manifest, CLI_VERSION, (w) => this.context.stderr.write(w + "\n"));

    // 5. Apply. Status → applying, then resolve every live input the converge engine
    //    needs and hand the whole thing to the health-gate wrapper.
    await patchApplyRequest(client, (r) => ({ ...r, status: "applying", phase: "starting", updatedAt: now() }));

    // Setup-to-gate region: everything between the "applying" write above and
    // the health-gate call below runs BEFORE any terminal status is written.
    // `runHealthGateWithRollback` always lands a terminal status itself (see
    // its doc comment) — success/held/rolled-back on every exit path — so it is
    // deliberately OUTSIDE this try. But a throw from setup (readStamp already
    // ran; a transient Azure throw in resolveFoundryProject / fetchManifest-
    // adjacent calls / recoverSuffix / resolveGatewayResourceId /
    // buildConvergeDeps) would otherwise leave the row at "claimed"/"applying"
    // forever — nothing else ever re-reads `leaseUntil` on a healthy tick. This
    // is the backstop: catch, patch → failed (but ONLY if the row is still
    // non-terminal — never clobber a status the inner code already finalized),
    // then rethrow so the command still exits non-zero and renders the error.
    let plan: ReturnType<typeof diffPlan>;
    let applyCtx: ApplyCtx;
    let deps: Awaited<ReturnType<typeof buildConvergeDeps>>;
    try {
      const project = await resolveFoundryProject({
        credential: ctx.credential,
        subscriptionId: ctx.subscriptionId,
        interactive: false,
        endpoint: ctx.endpoint,
      });
      // Content ← the baked repo when the marker resolved (the job's /opt/m8t/repo,
      // whose installer release we just confirmed matches the target above); else
      // fetch from the channel (the founder/dev path, which has GitHub auth). The
      // baked repo is a full repo-root-shaped checkout with personas/, so
      // resolveReleaseContent accepts it as `contentDir` and skips the fetch.
      // Both preconditions are the rail's contract; see rail-preconditions.ts for why
      // neither is a fallback worth taking in this job.
      assertBakedContentPresent(repoRoot);
      const contentDir = await resolveReleaseContentWithAgents(manifest, { contentDir: repoRoot });
      // Tree ← the manifest itself: when it carries components.infra.treeSha (every
      // manifest cut by the current generator does), infraTargetSha reads that value
      // and the RepoTree is never consulted — so a trivial stub avoids the authed
      // git-trees fetch entirely (matches update.ts/status.ts's --content-dir path).
      // A legacy manifest without infra.treeSha still fetches the real tree.
      assertForwardTarget(manifest);
      const tree: RepoTree = { treeShaOf: () => undefined };
      const suffix = await this.recoverSuffix(ctx);
      const gatewayResourceId = await this.resolveGatewayResourceId(ctx, suffix);

      deps = await buildConvergeDeps({
        credential: ctx.credential,
        subscriptionId: ctx.subscriptionId,
        resourceGroup: ctx.resourceGroup,
        contentDir,
        manifest,
        tree,
        project,
        gatewayResourceId,
        suffix,
        onProgress: (m) => {
          // Best-effort phase surfacing on the row (the banner reads it). A patch
          // failure here must not fail the converge — the terminal status still
          // lands via the health-gate wrapper.
          void patchApplyRequest(client, (r) => ({ ...r, phase: m, updatedAt: now() })).catch(() => undefined);
          log(m);
        },
        onWarn: (m) => this.context.stderr.write(m + "\n"),
      });

      // The updater job OWNS real infra: forceInfra so a deploy/ tree drift is a
      // real bicep apply (recoverSuffix guarantees a verified suffix), not the
      // "deferred" skip the interactive CLI takes.
      plan = diffPlan(manifest, stamp, tree, { forceInfra: true });

      // Include `suffix` in the ApplyCtx so the writeInfraParams chokepoint
      // fires on a full converge success (re-stamping system/infra-params).
      applyCtx = {
        credential: ctx.credential,
        subscriptionId: ctx.subscriptionId,
        resourceGroup: ctx.resourceGroup,
        contentDir,
        manifest,
        suffix,
        onProgress: (m) => {
          log(m);
        },
      };
    } catch (e) {
      await failIfStillInFlight(client, now, (e as Error).message);
      throw e;
    }

    const outcome = await runHealthGateWithRollback({
      client,
      deps,
      plan,
      ctx: applyCtx,
      stamp: stamp ?? seedStampFor(plan),
      manifest,
      credential: ctx.credential,
      endpoint: ctx.endpoint,
      // The rollback re-converge needs the SAME zero-repo-auth inputs as the
      // forward path: baked content (repoRoot) + the channel to resolve the
      // PREVIOUS version's manifest (never GitHub repo auth). Without these the
      // rollback would 404 on the private pre-OSS repo just like the forward
      // path did before this fix.
      channelUrl: ctx.channelUrl,
      repoRoot,
      now: () => new Date(),
    });

    log(`converge outcome: ${outcome}.`);
    return outcome === "success" ? 0 : 1;
  }

  /**
   * Recover the real bicep suffix WITHOUT guessing: prefer the stamped
   * `system/infra-params` row (written by enable-auto-update, and re-stamped
   * by every full converge), and fall back to deriving it from the live
   * gateway container-app name — but only when that derivation VERIFIES
   * (exactly one gateway + a matching CAE). If both fail, throw rather than
   * provision a duplicate stack.
   */
  private async recoverSuffix(ctx: HeadlessContext): Promise<string> {
    const infraTable = await openInfraParamsTable({
      credential: ctx.credential,
      subscriptionId: ctx.subscriptionId,
      resourceGroup: ctx.resourceGroup,
    });
    const stamped = (await readInfraParams(infraTable))?.suffix;
    const suffix =
      stamped ??
      (await deriveSuffixVerified({
        credential: ctx.credential,
        subscriptionId: ctx.subscriptionId,
        resourceGroup: ctx.resourceGroup,
      }));
    if (!suffix) {
      throw new LocalCliError({
        code: "PLATFORM_INFRA_SUFFIX_UNRECOVERABLE",
        message:
          "Could not recover the deployment's resource-name suffix: no system/infra-params row is stamped, and deriving it from the live gateway did not verify (0 or >1 gateway, or no matching CAE).",
        hint: "Run a full 'm8t platform update --force-infra --suffix <suffix>' once to stamp system/infra-params, then re-run the updater.",
      });
    }
    return suffix;
  }

  /** Resolve the gateway container-app ARM resource id for the recovered suffix. */
  private async resolveGatewayResourceId(ctx: HeadlessContext, suffix: string): Promise<string> {
    const id = (
      await runAz([
        "containerapp",
        "show",
        "-n",
        `m8t-gateway-${suffix}`,
        "-g",
        ctx.resourceGroup,
        "--subscription",
        ctx.subscriptionId,
        "--query",
        "id",
        "-o",
        "tsv",
      ])
    ).trim();
    if (!id) {
      throw new LocalCliError({
        code: "PLATFORM_GATEWAY_NOT_FOUND",
        message: `Could not resolve the gateway container app 'm8t-gateway-${suffix}' in resource group ${ctx.resourceGroup}.`,
      });
    }
    return id;
  }
}

/**
 * Build the rollback-baseline stamp the health-gate wrapper needs when there is
 * NO installed stamp yet (a first-ever converge). `runHealthGateWithRollback`
 * requires a non-null `InstalledStamp` and reads only `previousPlatformVersion`
 * off it for the rollback target. A first-ever converge has NOTHING installed to
 * roll back TO, so `previousPlatformVersion` is deliberately `null` (never the
 * manifest's declared predecessor, which was never installed here) — the
 * wrapper's "no previous version to roll back to" arm then records the failure
 * honestly instead of trying to roll onto a version that never landed.
 */
function seedStampFor(plan: ReturnType<typeof diffPlan>): InstalledStamp {
  return {
    schemaVersion: 1,
    platformVersion: plan.targetVersion,
    previousPlatformVersion: null,
    updatedAt: new Date().toISOString(),
    lastResult: "success",
    cli: CLI_VERSION,
    components: {
      gateway: { tag: "", digest: "", state: "managed" },
      codingAgent: { tag: "", digest: "", state: "managed" },
      azureExecutor: { tag: "", digest: "", state: "managed" },
      personas: {},
      infra: { treeSha: "" },
    },
  };
}

/**
 * The `ManifestSource` for the claimed target, PINNED to that exact version and
 * honoring a custom bicep-set channel host.
 *
 * The apply-request `target` is a bare version ("0.2.0"); a release manifest is
 * published as a per-version asset keyed by the git TAG (`platform-v0.2.0`).
 *   - Default channel (the canonical GitHub "latest" URL, or unset): use the
 *     canonical per-version release asset via `{ channel: true, version: tag }`
 *     → `channelUrlForVersion(tag)`.
 *   - Custom channel URL: derive the per-version URL by swapping the `latest`
 *     path segment for `download/<tag>` (the GitHub-release convention the
 *     custom mirror is expected to follow), so the pin is honored on the
 *     operator's host too. If the URL doesn't match that convention we fall back
 *     to fetching it directly — the version assertion at the call site still
 *     guarantees we only ever apply the claimed target.
 */
function targetManifestSource(ctx: HeadlessContext, target: string): ReturnType<typeof manifestSourceForVersionTag> {
  // manifestSourceForVersionTag normalizes bare/v/tag internally (via platformTag).
  return manifestSourceForVersionTag(ctx.channelUrl, target);
}
